import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ENDPOINTS } from '@/config/endpoints';
import { useRouter } from 'next/router';
import { pushCleverTapEvent } from '../pushCleverTapEvent';

const defaultFormData = {
  shipmentDate: null,
  shipmentType: 'export',
  originCountry: '',
  originCountryLabel: '',
  originPort: '',
  originPortLabel: '',
  destinationCountry: '',
  destinationCountryLabel: '',
  destinationPort: '',
  destinationPortLabel: '',
  commodity: '',
  commodityLabel: '',
  containerType: '',
  containerTypeLabel: '',
  unit: '',
  length: '',
  width: '',
  height: '',
  dimensionUnit: 'CM',
  grossWeight: '',
  grossWeightUnit: 'KG',
};

const StepOneForm = ({ formData, handleChange, handleDateChange, errors, setFormData, setErrors, loadType }) => {
  const [originCountryInput, setOriginCountryInput] = useState(formData?.originCountryLabel || '');
  const [destinationCountryInput, setDestinationCountryInput] = useState(formData?.destinationCountryLabel || '');
  const [originPortInput, setOriginPortInput] = useState(formData?.originPortLabel || '');
  const [destinationPortInput, setDestinationPortInput] = useState(formData?.destinationPortLabel || '');

  const [originCountryOptions, setOriginCountryOptions] = useState(
    formData?.originCountry && formData?.originCountryLabel
      ? [{ label: formData.originCountryLabel, value: formData.originCountry }]
      : []
  );
  const [destinationCountryOptions, setDestinationCountryOptions] = useState(
    formData?.destinationCountry && formData?.destinationCountryLabel
      ? [{ label: formData.destinationCountryLabel, value: formData.destinationCountry }]
      : []
  );
  const [originPortOptions, setOriginPortOptions] = useState(
    formData?.originPort && formData?.originPortLabel
      ? [{ label: formData.originPortLabel, value: formData.originPort }]
      : []
  );
  const [destinationPortOptions, setDestinationPortOptions] = useState(
    formData?.destinationPort && formData?.destinationPortLabel
      ? [{ label: formData.destinationPortLabel, value: formData.destinationPort }]
      : []
  );

  const [originPortLoading, setOriginPortLoading] = useState(false);
  const [destinationPortLoading, setDestinationPortLoading] = useState(false);
  const [showOriginCountryDropdown, setShowOriginCountryDropdown] = useState(false);
  const [showDestinationPortDropdown, setShowDestinationPortDropdown] = useState(false);

  const router = useRouter();

  // Sync input states with formData
  useEffect(() => {
    setOriginCountryInput(formData?.originCountryLabel || '');
    setDestinationCountryInput(formData?.destinationCountryLabel || '');
    setOriginPortInput(formData?.originPortLabel || '');
    setDestinationPortInput(formData?.destinationPortLabel || '');
  }, [
    formData?.originCountryLabel,
    formData?.destinationCountryLabel,
    formData?.originPortLabel,
    formData?.destinationPortLabel
  ]);

  // Fetch country/port lists on mount
  useEffect(() => {
    if (formData.originCountry && formData.originCountryLabel && originCountryOptions.length === 0) {
      fetch(ENDPOINTS.FETCHCOUNTRY + `?transportMode=ORIGIN&cargoType=FCL`)
        .then(res => res.json())
        .then(json => {
          const resultArr = Array.isArray(json?.data?.result) ? json.data.result : [];
          const formatted = resultArr.map(item => ({
            label: item.country_name,
            value: item.country_code
          }));
          setOriginCountryOptions(formatted);
        });
    }
    if (formData.destinationCountry && formData.destinationCountryLabel && destinationCountryOptions.length === 0) {
      fetch(`${ENDPOINTS.FETCHCOUNTRY}?transportMode=DESTINATION&cargoType=FCL&originCountryCode=${formData.originCountry}`)
        .then(res => res.json())
        .then(json => {
          const resultArr = Array.isArray(json?.data?.result) ? json.data.result : [];
          const formatted = resultArr.map(item => ({
            label: item.country_name,
            value: item.country_code
          }));
          setDestinationCountryOptions(formatted);
        });
    }
    if (formData.originPort && formData.originPortLabel && originPortOptions.length === 0) {
      setOriginPortLoading(true);
      fetchPorts(formData.originCountry, setOriginPortOptions, setOriginPortLoading);
    }
    if (formData.destinationPort && formData.destinationPortLabel && destinationPortOptions.length === 0) {
      setDestinationPortLoading(true);
      fetchDestinationPorts(formData.originCountry, formData.destinationCountry, formData.originPort, setDestinationPortOptions, setDestinationPortLoading);
    }
  }, []);

  // Fetch country/port lists when needed
  useEffect(() => {
    if (!formData.originCountry && originCountryOptions.length === 0) {
      fetch(ENDPOINTS.FETCHCOUNTRY + `?transportMode=ORIGIN&cargoType=FCL`)
        .then(res => res.json())
        .then(json => {
          const resultArr = Array.isArray(json?.data?.result) ? json.data.result : [];
          const formatted = resultArr.map(item => ({
            label: item.country_name,
            value: item.country_code
          }));
          setOriginCountryOptions(formatted);
        });
    }
  }, [formData.originCountry, originCountryOptions.length]);

  useEffect(() => {
    if (formData.originCountry && !formData.destinationCountry && destinationCountryOptions.length === 0) {
      fetch(`${ENDPOINTS.FETCHCOUNTRY}?transportMode=DESTINATION&cargoType=FCL&originCountryCode=${formData.originCountry}`)
        .then(res => res.json())
        .then(json => {
          const resultArr = Array.isArray(json?.data?.result) ? json.data.result : [];
          const formatted = resultArr.map(item => ({
            label: item.country_name,
            value: item.country_code
          }));
          setDestinationCountryOptions(formatted);
        });
    }
  }, [formData.originCountry, formData.destinationCountry, destinationCountryOptions.length]);

  useEffect(() => {
    if (formData.originCountry && !formData.originPort && originPortOptions.length === 0) {
      setOriginPortLoading(true);
      fetchPorts(formData.originCountry, setOriginPortOptions, setOriginPortLoading);
    }
  }, [formData.originCountry, formData.originPort, originPortOptions.length]);

  useEffect(() => {
    if (formData.originCountry && formData.destinationCountry && formData.originPort && !formData.destinationPort && destinationPortOptions.length === 0) {
      setDestinationPortLoading(true);
      fetchDestinationPorts(formData.originCountry, formData.destinationCountry, formData.originPort, setDestinationPortOptions, setDestinationPortLoading);
    }
  }, [formData.originCountry, formData.destinationCountry, formData.originPort, formData.destinationPort, destinationPortOptions.length]);

  const fetchPorts = async (countryCode, setOptions, setLoading) => {
    try {
      const res = await fetch(ENDPOINTS.FETCH_ORIGIN_PORT + `?transportMode=ORIGIN&cargoType=FCL&countryCode=${countryCode}`);
      const json = await res.json();
      const resultArr = Array.isArray(json?.data?.results?.data?.result) ? json.data.results.data.result : [];
      const formatted = resultArr.map(item => ({
        label: item.name,
        value: item.code
      }));
      setOptions(formatted);
    } catch (e) {
      console.error('Failed to fetch ports:', e);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinationPorts = async (originCountry, destinationCountry, originPort, setOptions, setLoading) => {
    if (!originCountry || !destinationCountry || !originPort) {
      setOptions([]);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(
        `https://credlix-mobileapi.credlix.com/shipping_est/locode?transportMode=DESTINATION&cargoType=FCL&originCountryCode=${originCountry}&countryCode=${destinationCountry}&originCountryLoCode=${originPort}`
      );
      const json = await res.json();
      const resultArr = Array.isArray(json?.data?.results?.data?.result) ? json.data.results.data.result : [];
      const formatted = resultArr.map(item => ({
        label: item.name,
        value: item.code
      }));
      setOptions(formatted);
    } catch (e) {
      console.error('Failed to fetch destination ports:', e);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (field, value, label) => {
    if (typeof setFormData === 'function') {
      setFormData(prev => {
        if (field === 'originCountry') {
          setOriginPortInput('');
          setDestinationPortInput('');
          setOriginPortOptions([]);
          setDestinationPortOptions([]);
          setDestinationCountryOptions([]);
          return {
            ...prev,
            [field]: value,
            [`${field}Label`]: label,
            originPort: '',
            originPortLabel: '',
            destinationPort: '',
            destinationPortLabel: '',
            destinationCountry: '',
            destinationCountryLabel: '',
          };
        } else if (field === 'destinationCountry') {
          setDestinationPortInput('');
          setDestinationPortOptions([]);
          return {
            ...prev,
            [field]: value,
            [`${field}Label`]: label,
            destinationPort: '',
            destinationPortLabel: '',
          };
        } else {
          return {
            ...prev,
            [field]: value,
            [`${field}Label`]: label
          };
        }
      });
      // Clear error for the selected field
      setErrors(prev => ({ ...prev, [field]: '' }));
    } else {
      console.warn('setFormData is not a function');
    }

    switch (field) {
      case 'originCountry':
        setOriginCountryInput(label);
        setShowOriginCountryDropdown(false);
        break;
      case 'destinationCountry':
        setDestinationCountryInput(label);
        break;
      case 'originPort':
        setOriginPortInput(label);
        break;
      case 'destinationPort':
        setDestinationPortInput(label);
        setShowDestinationPortDropdown(false);
        break;
    }
  };

  const handleShipmentTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      shipmentType: value
    }));
    setErrors(prev => ({ ...prev, shipmentType: '' }));
  };

  const handleInputChange = (field, setInput) => (e) => {
    const value = e.target.value;
    setInput(value);
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleCleverTapEvent = () => {
    if (loadType === 'FTL') {
      pushCleverTapEvent("Shipping_Calculator_Form_Start_FCL", { "PageName": 'Shipping_Calculator' })
    } else if (loadType === 'LTL') {
      pushCleverTapEvent("Shipping_Calculator_Form_Start_LCL", { "PageName": 'Shipping_Calculator' })
    }
  }

  return (
    <>
      <div className="flex items-center space-x-2 mb-2">
        <label className="flex items-center space-x-1">
          <input
            type="radio"
            name="shipmentType"
            value="export"
            checked={formData.shipmentType === 'export'}
            onChange={() => handleShipmentTypeChange('export')}
          />
          <span className="text-gray-700 text-sm">Export</span>
        </label>
        <label className="flex items-center space-x-1">
          <input
            type="radio"
            name="shipmentType"
            value="import"
            checked={formData.shipmentType === 'import'}
            onChange={() => handleShipmentTypeChange('import')}
          />
          <span className="text-gray-700 text-sm">Import</span>
        </label>
      </div>

      {/* Shipment Date */}
      <div className="mb-2 w-full">
        <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
          <svg className="mr-[5px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="grayText" width="18" height="18">
            <path d="M9 1V3H15V1H17V3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9ZM20 11H4V19H20V11ZM8 13V15H6V13H8ZM13 13V15H11V13H13ZM18 13V15H16V13H18ZM7 5H4V9H20V5H17V7H15V5H9V7H7V5Z"></path>
          </svg>
          Shipment Date <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center w-full space-x-4 shipment-date-picker">
          <DatePicker
            selected={formData.shipmentDate}
            onChange={(date) => {
              handleDateChange(date);
              setErrors(prev => ({ ...prev, shipmentDate: '' }));
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select a date"
            className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
            minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)} // Restrict to future dates only (tomorrow onwards)
            onCalendarOpen={handleCleverTapEvent}
          />
        </div>
        {errors.shipmentDate && <p className="text-red-600 text-xs mt-xsp">{errors.shipmentDate}</p>}
        {errors.shipmentType && <p className="text-red-600 text-xs mt-xsp">{errors.shipmentType}</p>}
      </div>

      <section className={
        `w-full flex flex-wrap ` +
        (router.pathname === '/shipment-calculator/quotes' ? 'gap-0' : 'gap-[10px]')
      }>
        {/* Origin Country */}
        <div
          className={
            `mb-2 relative ` +
            (router.pathname === '/shipment-calculator/quotes'
              ? 'w-full'
              : 'odd:w-[34%] even:w-[64%] sm:odd:w-full sm:even:w-full')
          }
        >
          <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
            <span className="flex items-center gap-.5">
              <svg className="mr-[5px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 20.8995L16.9497 15.9497C19.6834 13.2161 19.6834 8.78392 16.9497 6.05025C14.2161 3.31658 9.78392 3.31658 7.05025 6.05025C4.31658 8.78392 4.31658 13.2161 7.05025 15.9497L12 20.8995ZM12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364L12 23.7279ZM12 13C13.1046 13 14 12.1046 14 11C14 9.89543 13.1046 9 12 9C10.8954 9 10 9.89543 10 11C10 12.1046 10.8954 13 12 13ZM12 15C9.79086 15 8 13.2091 8 11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11C16 13.2091 14.2091 15 12 15Z"></path>
              </svg>
              Origin Country <span className="text-red-500">*</span>
            </span>
          </label>
          <input
            type="text"
            value={originCountryInput}
            onChange={handleInputChange('originCountry', setOriginCountryInput)}
            onFocus={() => setShowOriginCountryDropdown(true)}
            onBlur={() => setTimeout(() => setShowOriginCountryDropdown(false), 200)}
            placeholder="Search and select"
            className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
            autoComplete="off"
          />
          <span className="relative">
            <svg className="w-3 absolute top-1/2 right-2 -translate-y-1/2 text-grayText" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path></svg>
          </span>
          {showOriginCountryDropdown && originCountryOptions.length > 0 && (
            <ul className="absolute bg-white border border-gray-300 rounded w-full max-h-40 overflow-y-auto z-10">
              {originCountryOptions
                .filter(c => c.label.toLowerCase().includes(originCountryInput.toLowerCase()))
                .map(option => (
                  <li
                    key={option.value}
                    className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                    onMouseDown={() => {
                      handleSelect('originCountry', option.value, option.label);
                    }}
                  >
                    {option.label}
                  </li>
                ))}
            </ul>
          )}
          {errors?.originCountry && <p className="text-red-600 text-xs mt-xsp">{errors?.originCountry}</p>}
        </div>

        {/* Origin Port */}
        <AutoCompleteInput
          label={
            <span className="flex items-center gap-.5"> Port <span className="text-red-500">*</span>
              {originPortLoading && <span className="ml-2"><svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg></span>}
            </span>
          }
          inputValue={originPortInput}
          setInputValue={setOriginPortInput}
          options={originPortOptions}
          onSelect={item => handleSelect('originPort', item.value, item.label)}
          error={errors.originPort}
          fieldName="originPort"
          setErrors={setErrors}
        />

        {/* Destination Country */}
        <AutoCompleteInput
          label={
            <span className="flex items-center gap-.5">
              <svg className="mr-[5px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 20.8995L16.9497 15.9497C19.6834 13.2161 19.6834 8.78392 16.9497 6.05025C14.2161 3.31658 9.78392 3.31658 7.05025 6.05025C4.31658 8.78392 4.31658 13.2161 7.05025 15.9497L12 20.8995ZM12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364L12 23.7279ZM12 13C13.1046 13 14 12.1046 14 11C14 9.89543 13.1046 9 12 9C10.8954 9 10 9.89543 10 11C10 12.1046 10.8954 13 12 13ZM12 15C9.79086 15 8 13.2091 8 11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11C16 13.2091 14.2091 15 12 15Z"></path></svg>
              Destination Country <span className="text-red-500">*</span>
            </span>
          }
          inputValue={destinationCountryInput}
          setInputValue={setDestinationCountryInput}
          options={destinationCountryOptions}
          onSelect={item => handleSelect('destinationCountry', item.value, item.label)}
          error={errors.destinationCountry}
          fieldName="destinationCountry"
          setErrors={setErrors}
        />

        {/* Destination Port */}
        <div
          className={
            `mb-2 relative ` +
            (router.pathname === '/shipment-calculator/quotes'
              ? 'w-full'
              : 'odd:w-[34%] even:w-[64%] sm:odd:w-full sm:even:w-full')
          }
        >
          <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
            <span className="flex items-center gap-.5"> Port <span className="text-red-500">*</span>
              {destinationPortLoading && <span className="ml-2"><svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg></span>}
            </span>
          </label>
          <input
            type="text"
            value={destinationPortInput}
            onChange={handleInputChange('destinationPort', setDestinationPortInput)}
            onFocus={() => setShowDestinationPortDropdown(true)}
            onBlur={() => setTimeout(() => setShowDestinationPortDropdown(false), 200)}
            placeholder="Search and select"
            className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
            autoComplete="off"
          />
          <span className="relative">
            <svg className="w-3 absolute top-1/2 right-2 -translate-y-1/2 text-grayText" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path></svg>
          </span>
          {showDestinationPortDropdown && destinationPortOptions.length > 0 && (
            <ul className="absolute bg-white border border-gray-300 rounded w-full max-h-40 overflow-y-auto z-10">
              {destinationPortOptions
                .filter(c => c.label.toLowerCase().includes(destinationPortInput.toLowerCase()))
                .map(option => (
                  <li
                    key={option.value}
                    className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                    onMouseDown={() => {
                      handleSelect('destinationPort', option.value, option.label);
                    }}
                  >
                    {option.label}
                  </li>
                ))}
            </ul>
          )}
          {errors?.destinationPort && <p className="text-red-600 text-xs mt-xsp">{errors?.destinationPort}</p>}
        </div>
      </section>
    </>
  );
};

const AutoCompleteInput = ({ label, inputValue, setInputValue, options, onSelect, error, fieldName, setErrors }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const router = useRouter();

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(true);
    setErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleSelect = (option) => {
    onSelect(option);
    setInputValue(option.label);
    setShowDropdown(false);
  };

  const filteredOptions = inputValue.trim() === ''
    ? options
    : options.filter(c => c.label.toLowerCase().includes(inputValue.toLowerCase()));

  return (
    <div
      className={
        `mb-2 relative ` +
        (router.pathname === '/shipment-calculator/quotes'
          ? 'w-full'
          : 'odd:w-[34%] even:w-[64%] sm:odd:w-full sm:even:w-full')
      }
    >
      <label className="block text-sm font-medium mb-xsp text-grayText flex items-center">
        {label}
      </label>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        placeholder="Search and select"
        className="h-9 w-full px-[10px] py-[5px] text-dimgray border border-gray-light rounded-[4px] focus:outline-none focus:border-[#9C9C9C] text-sm"
      />
      <span className="relative">
        <svg className="w-3 absolute top-1/2 right-2 -translate-y-1/2 text-grayText" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path></svg>
      </span>
      {showDropdown && filteredOptions.length > 0 && (
        <ul className="absolute bg-white border border-gray-300 rounded w-full max-h-40 overflow-y-auto z-10">
          {filteredOptions.map(option => (
            <li
              key={option.value}
              className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
              onMouseDown={() => handleSelect(option)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-red-600 text-xs mt-xsp">{error}</p>}
    </div>
  );
};

export default StepOneForm;